import React from 'react'
import { useUIStore } from '@/stores/uiStore'
import { useKeyPress } from '@/hooks/useGlobalKeyCapture'

/**
 * "Arm on next hit" pill. When active, the next pad tap starts
 * recording into THAT pad (handled by PadGrid's trigger intercept).
 * A second tap on the same pad stops + assigns the sample.
 *
 * Visual states:
 *   - idle: neutral border, destructive on hover
 *   - armed: pulsing destructive background so the user remembers
 *     the next tap will record, not audition
 *
 * Keyboard: Shift+A toggles arm state anywhere in the app (modulo
 * text-input focus — typing "A" in a BPM field shouldn't arm).
 * When a recording is in flight, Shift+A / Escape both act as
 * cancel: the button clears the arm flag AND the active pad's
 * recordingPadIndex so the in-progress take is abandoned. The pad
 * record-hook's effect reconciles and stops the MediaRecorder.
 */
export const ArmRecordButton: React.FC = () => {
  const armed = useUIStore(s => s.recordArmNext)
  const setArmed = useUIStore(s => s.setRecordArmNext)
  const recordingPadIndex = useUIStore(s => s.recordingPadIndex)
  const setRecordingPadIndex = useUIStore(s => s.setRecordingPadIndex)
  const countingIn = useUIStore(s => s.recordingCountingIn)
  const countInRemainingSec = useUIStore(s => s.recordingCountInRemainingSec)
  const isRecording = recordingPadIndex !== null

  const toggle = () => {
    // If a take is running, treat ARM as a cancel gesture — clear
    // the pad index so the underlying useRecordToPad effect tears
    // down cleanly without saving, and drop the arm flag.
    if (isRecording) {
      setRecordingPadIndex(null)
      setArmed(false)
      return
    }
    setArmed(!armed)
  }

  useKeyPress({ key: 'a', shift: true }, toggle)
  useKeyPress({ key: 'Escape' }, () => {
    if (armed) setArmed(false)
    if (isRecording) setRecordingPadIndex(null)
  })

  // Three distinct visual states on one button so the user always
  // knows what's happening: idle ARM, armed (pulsing, waiting for
  // pad tap), counting in (pulsing with countdown), recording.
  const label = countingIn
    ? `CNT ${countInRemainingSec.toFixed(1)}s`
    : isRecording
      ? 'REC · tap pad to stop'
      : armed
        ? 'ARMED'
        : 'ARM'

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={armed || isRecording}
      title={
        countingIn
          ? `Count-in running — capture starts in ${countInRemainingSec.toFixed(1)}s. Esc to cancel.`
          : isRecording
            ? 'Recording in progress — tap the red pad to save + assign, or Esc to cancel.'
            : armed
              ? 'Armed — next pad tap starts recording into that pad. Tap again or Esc to cancel.'
              : 'Arm the next pad tap to record into it (Shift+A).'
      }
      className={`px-2.5 py-1 rounded-md border text-[10px] font-mono uppercase tracking-wider transition-colors ${
        countingIn
          ? 'bg-accent/20 border-accent text-accent animate-pulse'
          : armed || isRecording
            ? 'bg-destructive border-destructive text-white animate-pulse'
            : 'border-brd-subtle text-content-secondary hover:border-destructive hover:text-destructive'
      }`}
    >
      {label}
    </button>
  )
}
